/**
 * SSML Readme 画面 (`/ssml-readme`)。
 *
 * SSML の使いどころを参照するためのリファレンス画面。
 * SSML チューニング画面 (`/ssml-tuning`) から **別タブ** で開き、
 * デュアルモニターで並べて参照する運用を前提とする (`docs/ssml-tuning-plan.md` 4.2 / 8.2.1)。
 *
 * ## 画面構成 (2 タブ)
 *
 *   1. **よく使われる事例** (初期表示) … 「こんな読み方にしたい」から探す入口。
 *      課題 / 対策 / 利用 SSML / 期待効果 / 関連構文 をタグで絞り込める。
 *      同じタブの下部に **音声チューニングナレッジ** を置き、
 *      解決できた記録だけでなく **未解決の記録も状態付きで残す**
 *      (Readme を構文辞典ではなくナレッジベースとして育てるため)。
 *   2. **構文辞典** … 従来の 6 カテゴリ (強調 / 間 / 再生速度 /
 *      イントネーション / 音量 / その他) をタグ単位で引く辞典。
 *
 * ### なぜ事例を初期表示にするか
 *
 * 従来は構文カテゴリが入口だったが、それは「どのタグを使うか」を
 * 既に知っている人向けの索引でしかなかった。
 * 利用者の実際の入口は「駅名が聞き取りにくい」「お詫びの感じを出したい」
 * という課題であり、1 つの課題は複数タグの組み合わせで解決する。
 * そのため事例を先に見せ、構文辞典は事例の「関連構文」から辿る位置に置く。
 *
 * ## 設計上の約束
 *
 * - **データ取得 (fetch) を行わない**。表示するのは `components/ssmlReadme` の
 *   静的データのみで、ローディング / エラー状態を持たない (計画 0.7-(2))。
 *   API 障害でも Readme は必ず開けるため、SSML 編集の参照先として途切れない。
 * - 絞り込みの判定は `useCaseFilter.ts` の純関数に委譲する。本ファイルは
 *   状態 (選択中のタブ / カテゴリ / タグ) の保持と受け渡しだけを行う。
 *   将来の文字列検索追加時も、本ファイルは条件オブジェクトへ 1 項目足すだけで済む。
 * - Readme 自動育成 / DynamoDB / `GET /ssml-readme` / AI 連携は **Phase2 送り** (計画 8.7)。
 * - 別タブで幅を食わないよう、本文は **1 カラム固定** とする (計画 4.2)。
 */
import { useMemo, useState } from 'react';
import type React from 'react';

import {
  DEFAULT_SSML_README_CATEGORY,
  DEFAULT_SSML_README_SECTION,
  ReadmeCategoryNav,
  ReadmeEntryList,
  ReadmeSectionNav,
  SSML_README_CATEGORIES,
  SSML_README_LAST_UPDATED,
  SSML_README_SECTIONS,
  SSML_TUNING_KNOWLEDGE,
  SSML_USE_CASES,
  SSML_USE_CASE_TAGS,
  UseCasePanel,
  countUseCaseTags,
  filterKnowledge,
  filterUseCases,
  toggleUseCaseTag,
  type SsmlReadmeCategoryKey,
  type SsmlReadmeSectionKey,
  type SsmlUseCaseTag,
} from '../components/ssmlReadme';

export function SsmlReadmePage(): React.JSX.Element {
  /* 選択中のタブ。初期値は `sections.ts` が示す既定 (= よく使われる事例)。 */
  const [activeSection, setActiveSection] = useState<SsmlReadmeSectionKey>(
    DEFAULT_SSML_README_SECTION,
  );

  /* 構文辞典タブで選択中のカテゴリ (タブを離れても選択を保つ)。 */
  const [activeCategory, setActiveCategory] = useState<SsmlReadmeCategoryKey>(
    DEFAULT_SSML_README_CATEGORY,
  );

  /* 事例タブで選択中のタグ (空 = 絞り込みなし = 全件表示)。 */
  const [selectedTags, setSelectedTags] = useState<readonly SsmlUseCaseTag[]>([]);

  const activeSectionDefinition =
    SSML_README_SECTIONS.find((s) => s.section === activeSection) ?? SSML_README_SECTIONS[0];

  /*
   * 選択中カテゴリの実体。
   * データは静的定義なので必ず見つかるが、`find` の戻り値は undefined を含むため
   * 先頭カテゴリへフォールバックし、画面が空になる事故を防ぐ。
   */
  const currentCategory =
    SSML_README_CATEGORIES.find((c) => c.category === activeCategory) ?? SSML_README_CATEGORIES[0];

  /* 絞り込み条件はオブジェクトで作る (将来 keyword を足しても呼び出しが変わらない)。 */
  const filter = useMemo(() => ({ tags: selectedTags }), [selectedTags]);
  const visibleUseCases = useMemo(() => filterUseCases(SSML_USE_CASES, filter), [filter]);
  const visibleKnowledge = useMemo(() => filterKnowledge(SSML_TUNING_KNOWLEDGE, filter), [filter]);

  /*
   * タグの件数。0 件のタグはボタンに出さない
   * (押しても何も起きないタグを並べると、絞り込みが壊れて見える)。
   */
  const tagCounts = useMemo(() => countUseCaseTags(SSML_USE_CASES, SSML_TUNING_KNOWLEDGE), []);
  const availableTags = useMemo(
    () => SSML_USE_CASE_TAGS.filter((tag) => (tagCounts.get(tag.tag) ?? 0) > 0),
    [tagCounts],
  );

  /*
   * 事例の「関連構文」から構文辞典へ移動する導線。
   * タブとカテゴリを同時に切り替えることで、辿った先が目的のカテゴリになる
   * (タブだけ切り替えると、利用者は再度カテゴリを探すことになる)。
   */
  const handleSelectRelatedSyntax = (category: SsmlReadmeCategoryKey): void => {
    setActiveCategory(category);
    setActiveSection('syntax');
  };

  return (
    <div className="ssml-readme-page" data-testid="ssml-readme-page">
      <header className="ssml-readme-page__header">
        <h2 className="ssml-readme-page__title">SSML Readme</h2>
        <p className="ssml-readme-page__lead">
          「こんな読み方にしたい」から SSML の書き方を探せるリファレンスです。SSML
          チューニング画面と並べて参照できます (音声は Amazon Polly / 話者 Takumi / 日本語 / Neural
          エンジン)。
        </p>
        {/*
          R13: 静的データが実運用と乖離していないかを利用者が判断できるようにする。
          内容の追加・修正は PR で行い、自動では増えないことも明記する。
        */}
        <p className="ssml-readme-page__meta" data-testid="ssml-readme-last-updated">
          最終更新日: {SSML_README_LAST_UPDATED} / 内容は固定です (自動では増えません)。
        </p>
      </header>

      <ReadmeSectionNav
        sections={SSML_README_SECTIONS}
        activeSection={activeSection}
        onSelectSection={setActiveSection}
      />

      {activeSection === 'use-cases' ? (
        <UseCasePanel
          description={activeSectionDefinition.description}
          useCases={visibleUseCases}
          knowledge={visibleKnowledge}
          tags={availableTags}
          selectedTags={selectedTags}
          tagCounts={tagCounts}
          onToggleTag={(tag) => setSelectedTags((prev) => toggleUseCaseTag(prev, tag))}
          onClearTags={() => setSelectedTags([])}
          onSelectRelatedSyntax={handleSelectRelatedSyntax}
        />
      ) : (
        <div
          className="ssml-readme-syntax"
          role="tabpanel"
          id="ssml-readme-section-panel-syntax"
          aria-labelledby="ssml-readme-section-tab-syntax"
          data-testid="ssml-readme-section-panel-syntax"
        >
          <p className="ssml-readme-page__meta">{activeSectionDefinition.description}</p>
          <ReadmeCategoryNav
            categories={SSML_README_CATEGORIES}
            activeCategory={activeCategory}
            onSelectCategory={setActiveCategory}
          />
          <ReadmeEntryList category={currentCategory} />
        </div>
      )}
    </div>
  );
}
